import React, { useCallback, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import s from "./StatementPdf.module.scss";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import AsyncAutocomplete from "components/Input/AsyncAutocomplete/AsyncAutocomplete";
import useAsyncOptionField from "components/Input/AsyncAutocomplete/useAsyncOptionField";
import Selection from "components/Input/Selection/Selection";
import { useTranslation } from "react-i18next";
import { searchErrorHandler } from "utils/formValidation";
import { isPositiveInteger } from "utils/utils";
import usePageState from "utils/usePageState";

const buildMerchantOption = (merchant) => {
  const merchantId = `${merchant?.merchantId || ""}`.trim();
  const name = merchant?.name || "";
  const email = merchant?.email || "";

  if (!merchantId) return null;

  const label = [merchantId, name, email].filter(Boolean).join(" - ");

  return {
    key: merchantId,
    label: label || merchantId,
    value: merchantId,
    id: merchantId,
    name,
    email,
  };
};

const PAGE_LOCALE_KEY = "page.statement.";
const INPUT_FIELD_INIT_STATE = {
  merchantId: "",
  statementResponse: [],
  pdfPath: "",
  year: "",
  month: "",
};
const ERROR_INIT_STATE = {
  merchantId: false,
  statementResponse: false,
  pdfPath: false,
  year: false,
  month: false,
};

const StatementPdf = ({
  path,
  queryStatementList,
  showStatementPdf,
  generateStatementPdf,
  queryMerchantOptions,
}) => {
  const { pageState } = usePageState({ pagePath: path });
  const stateMerchantId = pageState?.id ? `${pageState.id}` : "";
  const isFromMerchantList = !!stateMerchantId;

  const [searchFieldInput, setSearchFieldInput] = useState({
    ...INPUT_FIELD_INIT_STATE,
    ...(stateMerchantId && { merchantId: stateMerchantId }),
  });
  const [focus, setFocus] = useState({});
  const [loading, setLoading] = useState({});
  const [btnDisable, setBtnDisable] = useState(false);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [error, setError] = useState(ERROR_INIT_STATE);

  const handleMerchantSelect = useCallback((nextMerchant) => {
    setSearchFieldInput((prev) => ({
      ...prev,
      merchantId: nextMerchant?.value || "",
    }));
    if (nextMerchant) {
      setError((prev) => ({ ...prev, merchantId: false }));
    }
  }, []);
  const getDefaultMerchantKeyword = useCallback(
    () => searchFieldInput.merchantId,
    [searchFieldInput.merchantId],
  );
  const isDefaultMerchantOption = useCallback(
    (option) => option.value === searchFieldInput.merchantId,
    [searchFieldInput.merchantId],
  );
  const {
    fetchOptions: fetchMerchantOptions,
    handleChange: handleMerchantChange,
    options: merchantOptions,
    selectedOption: selectedMerchant,
  } = useAsyncOptionField({
    queryOptions: queryMerchantOptions,
    mapOption: buildMerchantOption,
    getDefaultKeyword: getDefaultMerchantKeyword,
    isDefaultOption: isDefaultMerchantOption,
    onSelect: handleMerchantSelect,
  });

  useEffect(() => {
    if (!stateMerchantId) return;

    setSearchFieldInput((prev) =>
      prev.merchantId === stateMerchantId
        ? prev
        : { ...prev, merchantId: stateMerchantId },
    );
  }, [stateMerchantId]);

  const textInputOnChange = (e, field, callback) => {
    const value = callback ? callback(e.target.value) : e.target.value;
    setSearchFieldInput((prev) => ({ ...prev, [field]: value }));
  };

  const textInputOnFocus = (field) => {
    setFocus((prev) => ({ ...prev, [field]: true }));
  };

  const textInputOnBlur = (field, callback) => {
    if (searchFieldInput[field] && callback) {
      const isValidValue = callback(searchFieldInput[field]);
      if (!isValidValue) {
        setError((prev) => ({ ...prev, [field]: true }));
      }
    }
    setFocus((prev) => ({ ...prev, [field]: false }));
  };

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const handleOnClickViewPdfBtn = async (e, key) => {
    e?.preventDefault();
    e?.stopPropagation();

    const merchantId = stateMerchantId || searchFieldInput.merchantId;

    if (!isFromMerchantList) {
      const { errorObj, hasError } = searchErrorHandler(searchFieldInput, [
        (input) => !isPositiveInteger(input.merchantId) && { merchantId: true },
      ]);
      setError(errorObj);
      if (hasError) return;
    }

    setBtnLoading(key);
    setBtnDisable(true);

    try {
      const apiResponse = await queryStatementList({
        merchantId,
      });

      if (!apiResponse) {
        throw new Error("Network Error");
      }

      const { responseCode, data } = apiResponse.data;

      if (responseCode !== "0000") {
        throw new Error("Invalid Response");
      }

      if (!data) {
        snackbar(t(PAGE_LOCALE_KEY + "viewPdf.noRecord"), "warning");
      }

      setSearchFieldInput((prev) => ({
        ...prev,
        statementResponse: data,
        pdfPath: "",
      }));
    } catch (error) {
      snackbar(t("general.message.networkError"), "error");
    } finally {
      setBtnLoading(key);
      setBtnDisable(false);
    }
  };

  useEffect(() => {
    isFromMerchantList && handleOnClickViewPdfBtn(null, "viewPdfBtn");
  }, [stateMerchantId]);

  const handleOnClickStatement = async (e, period, key) => {
    e.preventDefault();
    e.stopPropagation();

    setBtnLoading(key);
    setBtnDisable(true);

    try {
      const { data } = await showStatementPdf({
        merchantId: Number(stateMerchantId || searchFieldInput.merchantId),
        period,
      });

      if (data.responseCode !== "0000") {
        throw new Error("Network Error");
      }

      if (!data.data?.url) {
        snackbar(t(PAGE_LOCALE_KEY + "viewPdf.noRecord"), "warning");
      }
      setSearchFieldInput((prev) => ({
        ...prev,
        pdfPath: data.data.url,
      }));
    } catch (error) {
      snackbar(t("general.message.networkError"), "error");
    } finally {
      setBtnLoading(key);
      setBtnDisable(false);
    }
  };

  const handleOnClickGeneratePdf = async (e, key) => {
    e.preventDefault();
    e.stopPropagation();

    const input = {
      ...searchFieldInput,
      merchantId: stateMerchantId || searchFieldInput.merchantId,
    };
    const { errorObj, hasError } = searchErrorHandler(input, [
      (input) => !isPositiveInteger(input.merchantId) && { merchantId: true },
      (input) => input.year === "" && { year: true },
      (input) => input.month === "" && { month: true },
    ]);

    setError(errorObj);
    if (hasError) return;

    setBtnLoading(key);
    setBtnDisable(true);

    try {
      const apiResponse = await generateStatementPdf({
        merchantId: Number(input.merchantId),
        period: `${searchFieldInput.year}-${searchFieldInput.month}`,
      });

      if (!apiResponse || apiResponse.data.responseCode !== "0000") {
        throw new Error("Network Error");
      }

      snackbar(t(PAGE_LOCALE_KEY + "generatePdf.success"), "success");
    } catch (error) {
      snackbar(t("general.message.networkError"), "error");
    } finally {
      setBtnLoading(key);
      setBtnDisable(false);
    }
  };

  const setBtnLoading = (btnKey) => {
    setLoading((prev) => ({ ...prev, [btnKey]: !prev[btnKey] }));
  };

  const renderStatementTable = () => {
    let groupedData = {};
    // Group periods by year
    if (
      searchFieldInput.statementResponse &&
      searchFieldInput.statementResponse.length > 0
    ) {
      groupedData = searchFieldInput.statementResponse.reduce((acc, item) => {
        const year = item.period.split("-")[0];
        if (!acc[year]) {
          acc[year] = [];
        }
        acc[year].push(item.period);
        return acc;
      }, {});
    }
    if (Object.keys(groupedData).length === 0) {
      return null;
    }
    const sortedYears = Object.keys(groupedData).sort((a, b) => b - a);

    return (
      <div style={{ maxWidth: "500px" }}>
        <h3
          style={{ fontWeight: "500" }}
        >{`${searchFieldInput.statementResponse[0]["merchant_name"]}'s Statement`}</h3>
        {sortedYears.map((year) => (
          <div key={year}>
            <h3 style={{ fontWeight: "500" }}>{year}</h3>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "10px",
                fontWeight: "500",
              }}
            >
              {groupedData[year].map((period) => {
                const [year, month] = period.split("-");
                const formattedPeriod = `${month}/${year}`;

                return (
                  <ButtonMAS
                    disabled={btnDisable}
                    isLoading={loading[formattedPeriod]}
                    className={`、
                      ${s.button} 
                      ${s.statementBtn} 
                      ${!loading[formattedPeriod] && btnDisable ? s.loading : ""}
                      `}
                    key={formattedPeriod}
                    variant="bordered"
                    onClick={(e) =>
                      handleOnClickStatement(e, period, formattedPeriod)
                    }
                  >
                    {formattedPeriod}
                  </ButtonMAS>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const generateLastNYears = (n) => {
    const currentYear = new Date().getFullYear();
    const years = [];

    for (let i = 0; i < n; i++) {
      const year = currentYear - i;
      years.push({
        key: year.toString(),
        title: year.toString(),
        value: year.toString(),
      });
    }
    return years;
  };

  const generateMonths = [
    { key: "01", title: "1", value: "01" },
    { key: "02", title: "2", value: "02" },
    { key: "03", title: "3", value: "03" },
    { key: "04", title: "4", value: "04" },
    { key: "05", title: "5", value: "05" },
    { key: "06", title: "6", value: "06" },
    { key: "07", title: "7", value: "07" },
    { key: "08", title: "8", value: "08" },
    { key: "09", title: "9", value: "09" },
    { key: "10", title: "10", value: "10" },
    { key: "11", title: "11", value: "11" },
    { key: "12", title: "12", value: "12" },
  ];

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        <div className={s.inputField}>
          {isFromMerchantList ? (
            <OutlinedText
              placeholder={t(PAGE_LOCALE_KEY + "viewPdf.merchantIdPlaceholder")}
              disabled
              value={searchFieldInput.merchantId}
              sxParams={{
                textFocus: focus.merchantId,
                textError: error.merchantId,
              }}
              onchangeFn={(e) => textInputOnChange(e, "merchantId")}
              onfocusFn={() => textInputOnFocus("merchantId")}
              onblurFn={() => textInputOnBlur("merchantId")}
              onEnterDownFn={(e) => handleOnClickViewPdfBtn(e, "viewPdfBtn")}
              errorMessage={
                error.merchantId
                  ? t(PAGE_LOCALE_KEY + "viewPdf.errorInput")
                  : ""
              }
            />
          ) : queryMerchantOptions ? (
            <AsyncAutocomplete
              fetchOptions={fetchMerchantOptions}
              preloadedOptions={merchantOptions}
              value={selectedMerchant}
              onChange={handleMerchantChange}
              placeholder={t(PAGE_LOCALE_KEY + "viewPdf.merchantIdPlaceholder")}
              disabled={btnDisable}
              error={!!error.merchantId}
              errorMessage={
                error.merchantId ? t(PAGE_LOCALE_KEY + "viewPdf.errorInput") : ""
              }
              sx={{ width: "100%" }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && selectedMerchant) {
                  handleOnClickViewPdfBtn(e, "viewPdfBtn");
                }
              }}
            />
          ) : (
            <OutlinedText
              placeholder={t(PAGE_LOCALE_KEY + "viewPdf.merchantIdPlaceholder")}
              disabled={btnDisable}
              value={searchFieldInput.merchantId}
              sxParams={{
                textFocus: focus.merchantId,
                textError: error.merchantId,
              }}
              onchangeFn={(e) => textInputOnChange(e, "merchantId")}
              onfocusFn={() => textInputOnFocus("merchantId")}
              onblurFn={() => textInputOnBlur("merchantId")}
              onEnterDownFn={(e) => handleOnClickViewPdfBtn(e, "viewPdfBtn")}
              errorMessage={
                error.merchantId
                  ? t(PAGE_LOCALE_KEY + "viewPdf.errorInput")
                  : ""
              }
            />
          )}
        </div>
        <div className={s.buttonContainer}>
          <ButtonMAS
            disabled={btnDisable}
            isLoading={loading?.viewPdfBtn}
            className={`${s.button} ${s.viewPdfBtn}`}
            onClick={(e) => handleOnClickViewPdfBtn(e, "viewPdfBtn")}
            key={"viewPdfBtn"}
            variant="primary"
          >
            {t(PAGE_LOCALE_KEY + "viewPdf.buttonTitle")}
          </ButtonMAS>
        </div>
        {searchFieldInput.statementResponse &&
          searchFieldInput.statementResponse.length > 0 ? (
          <div className={s.description}>
            {t(PAGE_LOCALE_KEY + "viewPdf.description")}
          </div>
        ) : (
          <div className={s.description}>
            {t(PAGE_LOCALE_KEY + "viewPdf.noStatementAvaible")}
          </div>
        )}

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            padding: "15px 0",
            gap: "15px",
          }}
        >
          {renderStatementTable()}
          {/* Displaying PDF statement here */}
          {searchFieldInput.pdfPath && (
            <div style={{ flex: "1 0", minWidth: "500px" }}>
              <object
                style={{ width: "100%", height: "1000px" }}
                data={searchFieldInput.pdfPath}
                type="application/pdf"
              ></object>
            </div>
          )}
        </div>
        <hr />
        <div className={s.buttonContainer}>
          <div className={s.yearMonthContainer}>
            <div className={s.inputField}>
              <Selection
                disabled={btnDisable}
                selectionValue={searchFieldInput.year}
                label="Year"
                options={generateLastNYears(10)}
                sxParams={{
                  selectionFocus: focus.year,
                  selectionError: error.year,
                }}
                onchangeFn={(e) => textInputOnChange(e, "year")}
                onfocusFn={() => textInputOnFocus("year")}
                onblurFn={() => textInputOnBlur("year")}
                errorMessage={
                  error.year
                    ? t(PAGE_LOCALE_KEY + "generatePdf.selectionError.year")
                    : ""
                }
              />
            </div>
            <div className={s.inputField}>
              <Selection
                selectionValue={searchFieldInput.month}
                label="Month"
                disabled={btnDisable}
                options={generateMonths}
                sxParams={{
                  selectionFocus: focus.month,
                  selectionError: error.month,
                }}
                onchangeFn={(e) => textInputOnChange(e, "month")}
                onfocusFn={() => textInputOnFocus("month")}
                onblurFn={() => textInputOnBlur("month")}
                errorMessage={
                  error.month
                    ? t(PAGE_LOCALE_KEY + "generatePdf.selectionError.month")
                    : ""
                }
              />
            </div>
          </div>
          <ButtonMAS
            disabled={btnDisable}
            isLoading={loading?.generatePdfBtn}
            className={`${s.button} ${s.generatePdfBtn}`}
            onClick={(e) => handleOnClickGeneratePdf(e, "generatePdfBtn")}
            key={"generatePdfBtn"}
            variant="primary"
          >
            {t(PAGE_LOCALE_KEY + "generatePdf.buttonTitle")}
          </ButtonMAS>
        </div>
        <div className={s.description}>
          {t(PAGE_LOCALE_KEY + "generatePdf.description")}
        </div>
      </div>
    </div>
  );
};

export default React.memo(StatementPdf);
